import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Camera, Image, ArrowRight, Search, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PWAHome = () => {
  const [accessCode, setAccessCode] = useState('');
  const [recentGalleries, setRecentGalleries] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Load recently viewed galleries from localStorage
    const recent = JSON.parse(localStorage.getItem('recentGalleries') || '[]');
    setRecentGalleries(recent.slice(0, 3));
  }, []);

  const handleAccessGallery = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/gallery/${accessCode.toUpperCase()}`);
      if (response.data) {
        // Save to recent galleries
        const recent = JSON.parse(localStorage.getItem('recentGalleries') || '[]');
        const newRecent = [
          { code: accessCode.toUpperCase(), name: response.data.gallery.name, timestamp: Date.now() },
          ...recent.filter(r => r.code !== accessCode.toUpperCase())
        ].slice(0, 5);
        localStorage.setItem('recentGalleries', JSON.stringify(newRecent));
        
        navigate(`/app/gallery/${accessCode.toUpperCase()}`);
      }
    } catch (error) {
      alert('Gallery not found. Please check your access code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 pt-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex justify-center mb-4">
          <div className="bg-amber-100 p-4 rounded-full">
            <Camera className="h-10 w-10 text-amber-600" />
          </div>
        </div>
        <h1 className="text-2xl font-heading text-stone-800">
          Bright Eyes Photography
        </h1>
        <p className="text-stone-500 text-sm">
          View your photos and place orders
        </p>
      </div>

      {/* Access Code Input */}
      <Card className="border-stone-200">
        <CardContent className="p-6">
          <form onSubmit={handleAccessGallery} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-stone-700">
                Enter Your Access Code
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                <Input
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="e.g. ABC12345"
                  className="pl-10 text-center text-lg tracking-widest uppercase font-mono"
                  maxLength={8}
                />
              </div>
              <p className="text-xs text-stone-500 text-center">
                Check your email for your unique gallery access code
              </p>
            </div>
            <Button 
              type="submit" 
              className="w-full btn-action"
              disabled={!accessCode.trim() || loading}
            >
              {loading ? 'Loading...' : 'View Gallery'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Recent Galleries */}
      {recentGalleries.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-stone-600 px-1">
            Recently Viewed
          </h2>
          <div className="space-y-2">
            {recentGalleries.map((gallery) => (
              <Link
                key={gallery.code}
                to={`/app/gallery/${gallery.code}`}
                className="block"
              >
                <Card className="border-stone-200 hover:border-amber-300 transition-colors">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="bg-stone-100 p-2 rounded-lg">
                      <Image className="h-6 w-6 text-stone-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800 truncate">
                        {gallery.name}
                      </p>
                      <p className="text-xs text-stone-500 font-mono">
                        {gallery.code}
                      </p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-stone-400" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Features */}
      <div className="grid grid-cols-2 gap-3 pt-4">
        <Card className="border-stone-200">
          <CardContent className="p-4 text-center">
            <div className="bg-amber-50 p-3 rounded-full w-fit mx-auto mb-2">
              <Image className="h-5 w-5 text-amber-600" />
            </div>
            <p className="text-sm font-medium text-stone-700">Browse Photos</p>
            <p className="text-xs text-stone-500 mt-1">View your gallery</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardContent className="p-4 text-center">
            <div className="bg-green-50 p-3 rounded-full w-fit mx-auto mb-2">
              <Sparkles className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-sm font-medium text-stone-700">Customize</p>
            <p className="text-xs text-stone-500 mt-1">Add backgrounds</p>
          </CardContent>
        </Card>
      </div>

      {/* Login Link */}
      <div className="text-center pt-4">
        <p className="text-sm text-stone-500">
          Have an account?{' '}
          <Link to="/app/account" className="text-amber-600 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default PWAHome;
